import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  actions as MyElearningStateActions,
  MyElearningStateType,
} from '@/modules/reducers/myElearning';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import {
  useCreateElearningMutation,
  useGenerateElearningFromFileMutation,
} from '@/modules/services/myElearnings';
import {
  getMonthlyCapMessage,
  useGetImportCapacityQuery,
  useImportPptFromStorageMutation,
} from '@/modules/services/pptImport';
import { uploadPptxToCkfinder } from '@/lib/util/ckfinderUpload';
import { useGetCurrentTenantProfileQuery } from '@/modules/services/auth';
import baseApi from '@/modules/services/base';
import { ElearningTag, MyElearningTag } from '@/modules/services/tags';
import config from '@/lib/config/index';
import { generatePath, useHistory } from 'react-router-dom';
import { useAiFeatureFlags } from '@/lib/featureFlags/useAiFeatureFlags';
import CreationMethodModal, {
  AiSetupValues,
  TargetLength,
} from './CreationMethodModal';
import { useAiCreateSubmit } from './useAiCreateSubmit';
import type { SubmitFileResult } from '@/modules/services/aiCreate';
import ManualModal from './ManualModal';
import PptConvertingModal from './PptConvertingModal';
import AiGeneratingModal from './AiGeneratingModal';
import AIGenerateModal from './AIGenerateModal';
import './index.scss';

const MODULE_SETUP_PATH = '/module/:elearningId/setup';

export const CREATION_METHODS = {
  MANUAL: 'manual',
  PPT: 'ppt',
  AI: 'ai',
} as const;

export type CreationMethod =
  (typeof CREATION_METHODS)[keyof typeof CREATION_METHODS];

const STEPS = {
  PICKER: 'picker',
  PPT_CONVERTING: 'ppt_converting',
  AI_GENERATING: 'ai_generating',
  AI: 'ai',
  MANUAL_FALLBACK: 'manual_fallback',
} as const;

type Step = (typeof STEPS)[keyof typeof STEPS];

const TRANSITION_MS = 250;

const NewModule = (): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  // AI creation methods require BOTH the LaunchDarkly rollout flag AND the
  // company-level "Allow AI Features" toggle (LMS2-5047). When off, the PPT/AI
  // cards are hidden and the picker falls back to the manual flow.
  const { isPptConversionEnabled, isCreateWithAiEnabled } = useAiFeatureFlags();

  // Tenant profile (cached app-wide) — the tenant UUID is used as the CKFinder
  // upload basedir (see handlePptImport).
  const { data: currentTenant } = useGetCurrentTenantProfileQuery();

  const isPickerMode = isPptConversionEnabled || isCreateWithAiEnabled;

  const [step, setStep] = useState<Step>(STEPS.PICKER);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // PPT converting state (set after import succeeds, drives PptConvertingModal)
  const [pptData, setPptData] = useState<{
    elearningId: string | null;
    totalSlides: number;
    name: string;
    fileName: string;
  } | null>(null);
  const [pptError, setPptError] = useState<string | null>(null);
  const [isPptImporting, setIsPptImporting] = useState(false);

  // AI generation state, set once the submit comes back 'started' (drives
  // AiGeneratingModal). The id is always known by then: the one call queues the
  // generation before it resolves, so there is no optimistic transition to cover.
  const [aiSetupValues, setAiSetupValues] = useState<AiSetupValues | null>(
    null,
  );
  // The files go with the request id, so a verdict from the worker can always be hung
  // on the files that request was submitted with (LMS2-5141).
  const [aiGenData, setAiGenData] = useState<{
    requestId: string;
    name: string;
    contextFiles: File[];
  } | null>(null);
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const myElearningState: MyElearningStateType = useSelector(
    (state: { myElearning: MyElearningStateType }) => state.myElearning,
  );
  const isOpen = myElearningState.isNewModuleOpen;

  const [createElearning, createElearningStatus] = useCreateElearningMutation();
  const [generateElearningFromFile, generateElearningStatus] =
    useGenerateElearningFromFileMutation();
  const [importPptFromStorage] = useImportPptFromStorageMutation();
  // Create-with-AI submit: checks and starts in one call (LMS2-5164). Scoped to
  // one open modal: reset in closeAll.
  const aiValidation = useAiCreateSubmit(currentTenant?.tenantId);
  const { reset: resetAiValidation, restoreDraft: restoreAiDraft } =
    aiValidation;
  // Up-front capacity check: blocks the upload UI when the tenant is already at
  // the concurrent-import limit (or monthly cap). Refetched each time the modal
  // opens; advisory only — the import endpoint re-checks atomically.
  const { data: importCapacity } = useGetImportCapacityQuery(undefined, {
    skip: !isOpen || !isPptConversionEnabled,
    refetchOnMountOrArgChange: true,
  });

  // ── Helpers ────────────────────────────────────────────────────────────────

  const closeAll = useCallback(() => {
    dispatch(MyElearningStateActions.closeNewModule());
    setStep(STEPS.PICKER);
    setIsTransitioning(false);
    setPptData(null);
    setPptError(null);
    setIsPptImporting(false);
    setAiGenData(null);
    setAiSetupValues(null);
    resetAiValidation();
  }, [dispatch, resetAiValidation]);

  const transitionTo = useCallback((nextStep: Step) => {
    setIsTransitioning(true);
    transitionTimer.current = setTimeout(() => {
      transitionTimer.current = null;
      setStep(nextStep);
      setIsTransitioning(false);
    }, TRANSITION_MS);
  }, []);

  // ── Navigate to editor after createElearning succeeds ──────────────────────
  useEffect(() => {
    if (createElearningStatus.isSuccess && createElearningStatus.data) {
      closeAll();
      history.push(
        generatePath(MODULE_SETUP_PATH, {
          elearningId: createElearningStatus.data.id,
        }),
      );
    }
  }, [
    createElearningStatus.isSuccess,
    createElearningStatus.data,
    closeAll,
    history,
  ]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleManualCreate = async (name: string) => {
    try {
      await createElearning({ name, tags: '' }).unwrap();
    } catch (err: any) {
      dispatch(
        flashMessageActions.addFlashMessage({
          message:
            (err?.data?.message as string) ??
            'Failed to create module. Please try again.',
          isActive: true,
        }),
      );
    }
  };

  const handlePptImport = async ({
    name,
    file,
  }: {
    name: string;
    file: File;
  }) => {
    setPptError(null);
    setIsPptImporting(true);

    setPptData({
      elearningId: null,
      totalSlides: 0,
      name,
      fileName: file.name,
    });
    transitionTo(STEPS.PPT_CONVERTING);

    try {
      const tenantId = currentTenant?.tenantId;
      if (!tenantId) {
        throw new Error(
          'Tenant is not resolved yet — please try again in a moment.',
        );
      }
      const { s3Key } = await uploadPptxToCkfinder(file, {
        tenantId,
        connectorPath: config.ckFinderConnectorPath,
      });

      const result = await importPptFromStorage({ s3Key, name }).unwrap();

      setPptData({
        elearningId: result.elearningId,
        totalSlides: result.slideCount,
        name,
        fileName: file.name,
      });
      setIsPptImporting(false);
    } catch (err: any) {
      const fallback = 'PowerPoint import failed. Please try again.';
      let msg: string;
      if (err?.data?.error === 'MONTHLY_CAP_EXCEEDED') {
        msg = getMonthlyCapMessage(err?.data?.cap);
      } else {
        const raw =
          err?.data?.message ?? (err instanceof Error ? err.message : null);
        msg = typeof raw === 'string' ? raw : fallback;
      }
      setPptError(msg);
      setIsPptImporting(false);
      if (transitionTimer.current) {
        clearTimeout(transitionTimer.current);
        transitionTimer.current = null;
      }
      setIsTransitioning(false);
      setStep(STEPS.PICKER);
    }
  };

  const handlePptDone = useCallback(
    (name: string) => {
      dispatch(
        flashMessageActions.addFlashMessage({
          message: `"${name}" is ready — open it in the editor to review.`,
          isActive: true,
        }),
      );
      closeAll();
      history.push('/my-library');
    },
    [dispatch, history, closeAll],
  );

  const handlePptError = useCallback(
    (message: string) => {
      closeAll();
      dispatch(
        flashMessageActions.addFlashMessage({ message, isActive: true }),
      );
    },
    [closeAll, dispatch],
  );

  const handlePptBackground = useCallback(
    (name: string) => {
      dispatch(
        flashMessageActions.addFlashMessage({
          message: `Converting in background. We'll let you know when "${name}" is ready.`,
          isActive: true,
        }),
      );
      closeAll();
    },
    [dispatch, closeAll],
  );

  const handleAiCreate = async ({
    name,
    prompt,
    targetLength,
    contextFiles,
    acceptContextFiles,
  }: {
    name: string;
    prompt: string;
    targetLength: TargetLength;
    contextFiles: File[];
    acceptContextFiles: File[];
  }) => {
    // One call: it validates and, on a clean pass, the generation is already
    // queued by the time this resolves. A rejection or a system error leaves the
    // form mounted with its messages — the hook owns both.
    const { outcome, requestId } = await aiValidation.run({
      name,
      prompt,
      targetLength,
      files: contextFiles,
      acceptFiles: acceptContextFiles,
    });
    if (outcome !== 'started' || !requestId) {
      return;
    }

    setAiSetupValues({ name, prompt, targetLength, contextFiles });
    setAiGenData({ requestId, name, contextFiles });
    transitionTo(STEPS.AI_GENERATING);
  };

  const handleAiGenDone = useCallback(
    (elearningId: string) => {
      // The module was created server-side mid-generation, so no mutation carries
      // the invalidation — refresh the library caches here (matches the sibling
      // create paths) so the new module is listed when the user returns.
      dispatch(baseApi.util.invalidateTags([ElearningTag, MyElearningTag]));
      closeAll();
      history.push(generatePath(MODULE_SETUP_PATH, { elearningId }));
    },
    [dispatch, closeAll, history],
  );

  const handleAiGenError = useCallback(
    (message: string) => {
      closeAll();
      dispatch(
        flashMessageActions.addFlashMessage({ message, isActive: true }),
      );
    },
    [closeAll, dispatch],
  );

  // A file the worker could not accept (LMS2-5141): back to the form with that file's
  // verdict on it. Unlike the failure path below, the draft is kept — the next click
  // continues it, and the server answers an unchanged form from what it already holds.
  const handleAiGenFileVerdict = useCallback(
    (files: SubmitFileResult[]) => {
      if (aiGenData) {
        restoreAiDraft(aiGenData.requestId, files, aiGenData.contextFiles);
      }
      setAiGenData(null);
      setStep(STEPS.PICKER);
    },
    [aiGenData, restoreAiDraft],
  );

  // Back to setup, not close: the failed run created nothing, so the admin returns to
  // the form with what they typed still in it. No flash, they just read the failure.
  const handleAiGenBackToSetup = useCallback(() => {
    setAiGenData(null);
    // The submit that failed deleted its staged uploads, so the returning form must
    // re-stage and re-check rather than reuse links that are already gone.
    resetAiValidation();
    setStep(STEPS.PICKER);
  }, [resetAiValidation]);

  const handleAIGenerate = async ({
    prompt,
    file,
  }: {
    prompt: string;
    file: File;
  }) => {
    try {
      const result = await generateElearningFromFile({
        prompt,
        file,
      }).unwrap();
      if (result.elearningId) {
        closeAll();
        history.push(
          generatePath(MODULE_SETUP_PATH, {
            elearningId: result.elearningId,
          }),
        );
      } else {
        dispatch(
          flashMessageActions.addFlashMessage({
            message: 'AI generation failed. Please try again.',
            isActive: true,
          }),
        );
      }
    } catch (err: any) {
      dispatch(
        flashMessageActions.addFlashMessage({
          message:
            (err?.data?.message as string) ??
            'AI generation failed. Please try again.',
          isActive: true,
        }),
      );
    }
  };

  const modalIsOpen = isOpen && !isTransitioning;

  // ── Render ─────────────────────────────────────────────────────────────────

  // No feature flags → fallback to simple manual modal
  if (!isPickerMode) {
    return (
      <ManualModal
        isOpened={modalIsOpen}
        onClose={closeAll}
        onSave={handleManualCreate}
        isLoading={createElearningStatus.isLoading}
        error={
          createElearningStatus.isError
            ? (createElearningStatus.error as any)?.data?.message ??
              'Failed to create module'
            : null
        }
      />
    );
  }

  switch (step) {
    case STEPS.PPT_CONVERTING:
      return pptData ? (
        <PptConvertingModal
          isOpened={modalIsOpen}
          elearningId={pptData.elearningId}
          totalSlides={pptData.totalSlides}
          name={pptData.name}
          fileName={pptData.fileName}
          uploadError={pptError}
          onDone={handlePptDone}
          onBackground={handlePptBackground}
          onError={handlePptError}
        />
      ) : null;

    case STEPS.AI_GENERATING:
      return aiGenData ? (
        <AiGeneratingModal
          isOpened={modalIsOpen}
          requestId={aiGenData.requestId}
          name={aiGenData.name}
          onDone={handleAiGenDone}
          onError={handleAiGenError}
          onBackToSetup={handleAiGenBackToSetup}
          onFileVerdict={handleAiGenFileVerdict}
        />
      ) : null;

    case STEPS.AI:
      return (
        <AIGenerateModal
          isOpened={modalIsOpen}
          onBack={() => transitionTo(STEPS.PICKER)}
          onClose={closeAll}
          onGenerate={handleAIGenerate}
          isLoading={generateElearningStatus.isLoading}
        />
      );

    case STEPS.PICKER:
    default:
      return (
        <CreationMethodModal
          isOpened={modalIsOpen}
          onClose={closeAll}
          initialAiValues={aiSetupValues}
          onAiCreate={handleAiCreate}
          aiValidation={aiValidation.view}
          onContextFileRemoved={aiValidation.forgetFile}
          onAiValidationReset={resetAiValidation}
          onManualCreate={handleManualCreate}
          onPptImport={handlePptImport}
          isPptConversionEnabled={isPptConversionEnabled}
          isCreateWithAiEnabled={isCreateWithAiEnabled}
          isCreateLoading={createElearningStatus.isLoading}
          isPptImporting={isPptImporting}
          importCapacity={importCapacity}
          createError={
            createElearningStatus.isError
              ? (createElearningStatus.error as any)?.data?.message ??
                'Failed to create module'
              : null
          }
          pptError={pptError}
        />
      );
  }
};

export default NewModule;
